import React, { useState } from 'react';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import IconButton from '@welldev-group/rcl-icon-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { Alert, CheckBox, I18nText, Spinner } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import SpinnerIcon from 'affiliateIcons/spinner.svg';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import ChannelSpecificForm from './ChannelSpecificForm';

import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

const ChannelEditModal = ({
  fetchCredentials,
  affiliateId,
  supplier,
  id,
  humanizedSupplier,
  supplierSpecific,
  setIdentifierOnChange,
  identifier,
  setForFlightOnChange,
  forFlight,
  setForHotelOnChange,
  forHotel,
  setForCarOnChange,
  forCar,
  setSupplierParamsOnChange,
  supplierParams,
  clearStateOnModalClose,
  isSupplierSelected,
  setSupplierSpecificAttributesOnFetch,
  isFetching,
  setIsFetchingOnChange,
  enabled,
  setEnabled,
  canEnableOrDisable,
  disabled,
}) => {
  const [error, setError] = useState({});
  const [editDisable, setEditDisable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cityId, setCityId] = useState(null);
  const [updatedOptions, setUpdatedOptions] = useState([]);

  const fetchSupplierSpecificFormAttributes = async () => {
    setIsFetchingOnChange(true);
    const editData = await httpClient.get(
      routes.credentials.edit({
        supplier,
        id,
      }),
    );
    if (editData.data.cityIdentifier) {
      const cityRes = await httpClient.get(
        routes.cities.single({
          city_id: editData.data.cityIdentifier,
        }),
      );
      setCityId(cityRes.data.value);
      setUpdatedOptions([cityRes.data]);
    }
    setSupplierSpecificAttributesOnFetch(editData.data);
  };

  const onModalClose = () => {
    clearStateOnModalClose();
    setModalOpen(false);
  };

  const onModalOpen = () => {
    fetchSupplierSpecificFormAttributes();
    setModalOpen(true);
  };

  const onChangeCityId = option => {
    setCityId(option.value);
  };
  const loadCities = async (inputValue = '') => {
    if (inputValue.length >= 2) {
      const { data } = await httpClient.get(
        routes.cities.list({
          city_name: inputValue,
        }),
      );
      return data;
    }
    return null;
  };

  const onSearchCities = debounce(1000, false, async (inputValue, callback) => {
    const options = await loadCities(inputValue);
    setUpdatedOptions(options);

    if (options && options.length > 0) {
      callback(options);
    }
  });

  const loadOptions = (inputValue, callback) => {
    onSearchCities(inputValue, callback);
  };

  const updateCredential = async params => {
    httpClient
      .put(routes.credentials.update({ supplier, id }), params)
      .then(({ data }) => {
        if (data.error === null) {
          fetchCredentials(affiliateId);
          setModalOpen(false);
          setEditDisable(false);
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          setError(response.data.error);
          setEditDisable(false);
        }
      });
  };

  const handleUpdateCredential = () => {
    const params = {};
    supplierParams.forEach(param => {
      params[param.key] = param.value;
    });
    params.type = supplierSpecific.type;
    params.enabled = enabled;
    params.account_identifier = identifier;
    params.city_identifier = cityId;

    if (supplierSpecific.isFilteringEnabled) {
      params.flight = forFlight ? 1 : 0;
      params.hotel = forHotel ? 1 : 0;
      params.car = forCar ? 1 : 0;
    }
    const credentialParams = {};
    credentialParams[supplierSpecific.formNamespace] = params;
    setEditDisable(true);
    updateCredential(credentialParams);
  };

  return (
    <div className="channel-modal__container">
      <IconButton
        icon={<Icon name="edit" />}
        size="large"
        version="v2"
        standalone={true}
        onClick={onModalOpen}
        disabled={disabled}
      />
      <Modal
        className="modal__wrapper modal__wrapper-small modal__wrapper-overflow modal__wrapper-overflow-vertical"
        isVisible={modalOpen}
        onOutsideClick={onModalClose}
      >
        <div className="modal__header">
          <Modal.Title>
            {I18n.t('components.channel.edit_modal.header', {
              humanized_supplier: humanizedSupplier,
            })}
          </Modal.Title>
          <div
            onClick={onModalClose}
            onKeyDown={onModalClose}
            role="presentation"
          >
            <Icon name="close" size="normal" />
          </div>
        </div>
        <Modal.Content>
          <div className="col-12 col-bleed">
            {isSupplierSelected && supplierSpecific ? (
              <>
                <SelectBox
                  isAsync={true}
                  loadOptions={loadOptions}
                  label={<I18nText id="channel.label.pos_city" />}
                  value={selectBoxObjectMapper(updatedOptions, cityId)}
                  onChange={onChangeCityId}
                  isClearable={false}
                  cacheOptions={true}
                  width="full"
                />
                <ChannelSpecificForm
                  supplierSpecific={supplierSpecific}
                  setIdentifierOnChange={setIdentifierOnChange}
                  identifier={identifier}
                  setForFlightOnChange={setForFlightOnChange}
                  forFlight={forFlight}
                  setForHotelOnChange={setForHotelOnChange}
                  forHotel={forHotel}
                  setForCarOnChange={setForCarOnChange}
                  forCar={forCar}
                  setSupplierParamsOnChange={setSupplierParamsOnChange}
                  supplierParams={supplierParams}
                  error={error}
                />
              </>
            ) : (
              isFetching && (
                <div className="col-12 channel-modal__loading-spinner">
                  <Spinner color="black" size="tiny" />
                </div>
              )
            )}
            <Alert isVisible={!!error.base} hideClose={true} type="danger">
              {error.base}
            </Alert>
            <div className="col-12 col-bleed-x channel-modal__fields">
              <CheckBox
                label={<I18nText id="channel.label.enable_channel" />}
                onChange={event => setEnabled(event.target.checked)}
                checked={enabled}
                disabled={!canEnableOrDisable}
              />
            </div>
          </div>
          <div className="col-12 col-bleed-x channel-modal__fields channel-modal__buttons">
            <Button
              onClick={handleUpdateCredential}
              label={
                editDisable ? (
                  <>
                    <I18nText id="channel.label.editing" />
                    <img
                      src={SpinnerIcon}
                      alt="spinner"
                      className="channel-modal__spinner"
                    />
                  </>
                ) : (
                  <I18nText id="channel.label.edit" />
                )
              }
              version="v2"
              size="small"
              type="primary"
              disabled={editDisable}
            />

            <Button
              onClick={onModalClose}
              label={<I18nText id="channel.label.cancel" />}
              version="v2"
              size="small"
            />
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

ChannelEditModal.defaultProps = {
  supplierSpecific: null,
  forFlight: null,
  forHotel: null,
  forCar: null,
  identifier: null,
  supplierParams: null,
  disabled: false,
};

ChannelEditModal.propTypes = {
  fetchCredentials: PropTypes.func.isRequired,
  affiliateId: PropTypes.number.isRequired,
  supplier: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  humanizedSupplier: PropTypes.string.isRequired,
  supplierSpecific: PropTypes.shape({
    accountIdentifier: PropTypes.string,
    forCar: PropTypes.bool,
    forFlight: PropTypes.bool,
    forHotel: PropTypes.bool,
    formNamespace: PropTypes.string,
    isFilteringEnabled: PropTypes.bool,
    type: PropTypes.string,
  }),
  setIdentifierOnChange: PropTypes.func.isRequired,
  identifier: PropTypes.string,
  setForFlightOnChange: PropTypes.func.isRequired,
  forFlight: PropTypes.bool,
  setForHotelOnChange: PropTypes.func.isRequired,
  forHotel: PropTypes.bool,
  setForCarOnChange: PropTypes.func.isRequired,
  forCar: PropTypes.bool,
  setSupplierParamsOnChange: PropTypes.func.isRequired,
  supplierParams: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  clearStateOnModalClose: PropTypes.func.isRequired,
  isSupplierSelected: PropTypes.bool.isRequired,
  setSupplierSpecificAttributesOnFetch: PropTypes.func.isRequired,
  setIsFetchingOnChange: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  setEnabled: PropTypes.func.isRequired,
  canEnableOrDisable: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
};

export default ChannelEditModal;
